import { useEffect, useRef, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Progress,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
} from "@chakra-ui/react";
import moment from "moment";
import VendasBolePixLoteApi from "../../../../services/VendasBoletoloteApi";
import { dateForList } from "../../../../constants/dataFormat";

interface Props {
  idLote: string;
  // compact = só barra + contagens (sem a tabela por parcela).
  compact?: boolean;
}

const badgeStatus = (status: string) => {
  if (status === "EMITIDO") return <Badge colorScheme="green">Emitido</Badge>;
  if (status === "FALHA") return <Badge colorScheme="red">Falha</Badge>;
  return <Badge colorScheme="yellow">Pendente</Badge>;
};

export default function LoteStatusProgresso({ idLote, compact }: Props) {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<any>(null);

  useEffect(() => {
    if (!idLote) return;

    let ativo = true;
    const carregar = async () => {
      try {
        const data = await VendasBolePixLoteApi.getStatusLote(idLote);
        if (!ativo) return;
        setStatus(data);
        // Para de pollar quando não há mais parcelas pendentes.
        if (data?.concluido && timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      } catch (e) {
        // silencioso — tenta de novo no próximo ciclo
      } finally {
        if (ativo) setLoading(false);
      }
    };

    carregar();
    timerRef.current = setInterval(carregar, 4000);
    return () => {
      ativo = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [idLote]);

  if (loading && !status) {
    return (
      <Flex align="center" gap={2} py={4}>
        <Spinner size="sm" />
        <Text>Carregando status da geração…</Text>
      </Flex>
    );
  }
  if (!status) return null;

  const total = status.total || 0;
  const emitidas = status.emitidas || 0;
  const pct = total > 0 ? Math.round((emitidas / total) * 100) : 0;

  return (
    <Box>
      <Flex justify="space-between" mb={1} fontSize="sm">
        <Text fontWeight="medium">
          {emitidas} de {total} emitidos{" "}
          {status.concluido ? "" : "— gerando as demais…"}
        </Text>
        <Text fontWeight="bold">{pct}%</Text>
      </Flex>
      <Progress
        value={pct}
        colorScheme={status.falhas > 0 ? "orange" : "green"}
        borderRadius="md"
        mb={3}
        hasStripe
        isAnimated={!status.concluido}
      />

      <Flex gap={2} mb={3} flexWrap="wrap">
        <Badge colorScheme="green">Emitidos: {emitidas}</Badge>
        <Badge colorScheme="yellow">Pendentes: {status.pendentes}</Badge>
        <Badge colorScheme="red">Falhas: {status.falhas}</Badge>
      </Flex>

      {!compact && (
      <Box
        maxH="280px"
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
      >
        <Table size="sm" variant="simple">
          <Thead position="sticky" top={0} bg="white" zIndex={1}>
            <Tr>
              <Th>Parcela</Th>
              <Th>Vencimento</Th>
              <Th isNumeric>Valor</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {status.parcelas?.map((p: any) => (
              <Tr key={p.id}>
                <Td>{p.parcela === 1 ? "1ª (entrada)" : `${p.parcela}ª`}</Td>
                <Td>{moment(p.dataVencimento).format(dateForList)}</Td>
                <Td isNumeric>
                  {Number(p.valor).toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
                </Td>
                <Td>
                  {badgeStatus(p.status)}
                  {p.status === "FALHA" && p.ultimoErro && (
                    <Text fontSize="xs" color="red.500">
                      {p.ultimoErro}
                    </Text>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      )}
    </Box>
  );
}
